import React, { useEffect, useState } from 'react'
import {
	SignedIn,
	SignedOut,
	RedirectToSignIn,
	useAuth,
} from '@clerk/react-router'
import { Navigate } from 'react-router-dom'
import {
	clearCounselorLoginGrant,
	finalizePendingCounselorGrant,
	getPendingCounselorGrant,
	isCounselorLoginGrantValid,
} from 'shared/auth/counselorLoginGrant'
import { counselorSessionStore } from 'shared/stores/counselorSessionStore'

/**
 * Requires a session-bound Counselor login grant (not Clerk SignedIn alone).
 * Does not signOut Student sessions; redirects to /auth when grant is missing/invalid.
 */
function CounselorEntryGate({ children }) {
	const { isLoaded, sessionId, userId } = useAuth()
	const [status, setStatus] = useState('checking')

	useEffect(() => {
		if (!isLoaded) return undefined

		let alive = true

		const run = async () => {
			if (getPendingCounselorGrant() && sessionId && userId) {
				finalizePendingCounselorGrant({ sessionId, userId })
			}

			if (!isCounselorLoginGrantValid({ sessionId, userId })) {
				if (alive) setStatus('denied')
				return
			}

			try {
				await counselorSessionStore.ensureLoaded({ force: true })
				if (!alive) return
				if (
					counselorSessionStore.loadStatus === 'error' ||
					!counselorSessionStore.profile
				) {
					clearCounselorLoginGrant()
					setStatus('denied')
					return
				}
				setStatus('allowed')
			} catch {
				if (!alive) return
				clearCounselorLoginGrant()
				setStatus('denied')
			}
		}

		setStatus('checking')
		run()

		return () => {
			alive = false
		}
	}, [isLoaded, sessionId, userId])

	if (!isLoaded || status === 'checking') {
		return null
	}

	if (status === 'denied') {
		return <Navigate to="/auth" replace />
	}

	return children
}

export default function Protected({ children }) {
	const redirectBack =
		typeof window !== 'undefined'
			? window.location.pathname + window.location.search + window.location.hash
			: '/'

	return (
		<>
			<SignedIn>
				<CounselorEntryGate>{children}</CounselorEntryGate>
			</SignedIn>
			<SignedOut>
				<RedirectToSignIn signInUrl="/auth" redirectUrl={redirectBack} />
			</SignedOut>
		</>
	)
}
